import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { appendCapiumTag, capiumTagGroupsFor } from '@/core/models/capium-tags';
import type {
  FirmNoteTextRow,
  ReportNotesKey,
  ReportNotesSettings,
  ReportNotesSettingsResponse,
} from '@/core/models/firm-settings.models';
import type { NoteEditorDialogData, NoteEditorDialogResult } from '@/core/models/report-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { firmSettingsService } from '@/core/services/firmSettings';
import { PrinciplesActivitesDialog } from '@/features/tr-reportsettings/PrinciplesActivitesDialog';
import { Button } from '@/kit/Button';
import { openDialog } from '@/kit/dialog';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Radio, RadioGroup } from '@/kit/Radio';
import { getNoteDefault, getNoteTitle } from './generalSettingsNoteCatalogue';
import { validateForm } from './validateForm';

export type ReportNotesListField =
  'reportPoints' | 'accountantReport' | 'auditorReport' | 'profitAndLossPolicies' | 'balanceSheetPolicies';

export type ReportNotesField = ReportNotesListField | 'accountantReportType' | 'auditorReportType';

export interface ReportNotesLayout {
  /** Number of inputs the screen shows for each list (must match the API layout for the key). */
  counts: Record<ReportNotesListField, number>;
  /**
   * Each tab's fields. Every field has its own independent edit/save pencil (owner-requested addition, see
   * PARITY_DECISIONS); this mapping now only drives the Capium Tags panel's visibility (`isEditingAny`) and
   * `insertTag`'s target field.
   */
  tabs: Record<string, ReportNotesField[]>;
  /** Controls drawn indented as sub-items of the row above (limited company: Goodwill .. under Intangible assets). */
  indented?: readonly string[];
}

/** Form control prefix per list; controls are named prefix + 1-based position (e.g. plPolicy3). */
const PREFIX: Record<ReportNotesListField, string> = {
  reportPoints: 'reportPoint',
  accountantReport: 'accountantReport',
  auditorReport: 'auditorReport',
  profitAndLossPolicies: 'plPolicy',
  balanceSheetPolicies: 'bsPolicy',
};

const LIST_FIELDS: ReportNotesListField[] = [
  'reportPoints',
  'accountantReport',
  'auditorReport',
  'profitAndLossPolicies',
  'balanceSheetPolicies',
];

/** Director's/member's report, accountant's and auditor's reports and policies (limited companies and LLPs). */
export function incorporatedLayout(
  counts: Record<ReportNotesListField, number>,
  indented: readonly string[] = [],
): ReportNotesLayout {
  return {
    counts,
    tabs: {
      report: ['reportPoints'],
      accountant: ['accountantReportType', 'accountantReport', 'auditorReportType', 'auditorReport'],
      policy: ['profitAndLossPolicies', 'balanceSheetPolicies'],
    },
    indented,
  };
}

/** Partnership and sole trader: a two-line accountant's report and the accounting policies (26 total, BUG-007 round 2). */
export const SMALL_BUSINESS_LAYOUT: ReportNotesLayout = {
  counts: {
    reportPoints: 0,
    accountantReport: 2,
    auditorReport: 0,
    profitAndLossPolicies: 12,
    balanceSheetPolicies: 14,
  },
  tabs: {
    accountant: ['accountantReport'],
    policy: ['profitAndLossPolicies', 'balanceSheetPolicies'],
  },
  indented: ['bsPolicy2', 'bsPolicy3', 'bsPolicy4', 'bsPolicy5'],
};

type Values = Record<string, string>;

function controlNames(layout: ReportNotesLayout, fields: ReportNotesField[]): string[] {
  return fields.flatMap((field) => {
    if (field === 'accountantReportType' || field === 'auditorReportType') {
      return [field];
    }
    return Array.from({ length: layout.counts[field] }, (_, i) => `${PREFIX[field]}${i + 1}`);
  });
}

function allControlNames(layout: ReportNotesLayout): string[] {
  return [
    'accountantReportType',
    'auditorReportType',
    ...LIST_FIELDS.flatMap((field) => controlNames(layout, [field])),
  ];
}

/** A tab's control names, excluding the accountant/auditor report-type radios (not a Capium Tags insertion target). */
function textControlNames(layout: ReportNotesLayout, tab: string): string[] {
  return controlNames(layout, layout.tabs[tab] ?? []).filter((name) => !name.endsWith('ReportType'));
}

const LEGACY_LLP_REPORT_POINTS = new Set([
  "Members' responsibilities statement",
  'Disclosure of information to auditors',
  'Auditors',
  'Small LLP exemptions',
  'Designated members',
  "Members' drawings contributions and repayments",
]);

const LEGACY_LLP_ACCOUNTANT_REPORT = new Set([
  'Exemption from audit',
  "Members' responsibilities",
  'Special provisions',
  "Accountant's report on the unaudited financial statements",
  'Respective responsibilities of directors and accountant',
  'Scope of work',
  'Independence and ethical standards',
  'Basis of opinion',
]);

const LEGACY_LLP_AUDITOR_REPORT = new Set([
  'Opinion',
  'Basis for opinion',
  "Independent auditor's report",
  'Opinion on financial statements',
  'Conclusions relating to going concern',
  'Other information',
  'Responsibilities of directors',
]);

function initialValues(layout: ReportNotesLayout): Values {
  const values: Values = {};
  for (const name of allControlNames(layout)) {
    values[name] = name.endsWith('ReportType') ? '0' : '';
  }
  return values;
}

/** Each control's tab, used by `isEditingAny` to gate that tab's Capium Tags panel. */
function tabOfControls(layout: ReportNotesLayout): Record<string, string> {
  const map: Record<string, string> = {};
  for (const [tab, fields] of Object.entries(layout.tabs)) {
    for (const name of controlNames(layout, fields)) {
      map[name] = tab;
    }
  }
  return map;
}

function toValues(
  layout: ReportNotesLayout,
  response: ReportNotesSettingsResponse,
  settingsKey?: ReportNotesKey,
): Values {
  const settings = response.settings;
  const values: Values = {
    accountantReportType: String(settings.accountantReportType ?? 0),
    auditorReportType: String(settings.auditorReportType ?? 0),
  };
  const key = settingsKey ?? response.key;
  const isLlp = key === 'llp';

  // BUG-004/005: the API converts a saved list still holding the earlier default titles (slot by slot, keeping any edited
  // title). Here only an uncustomised or empty list, or a single slot still holding an earlier title, shows the catalogue
  // title - a firm's edited titles in the other slots are never replaced.
  const hasLegacyLlpPoints = isLlp && (!response.isCustomised || !settings.reportPoints?.length);

  const hasLegacyLlpAccountant = isLlp && (!response.isCustomised || !settings.accountantReport?.length);

  const hasLegacyLlpAuditor = isLlp && (!response.isCustomised || !settings.auditorReport?.length);

  for (const field of LIST_FIELDS) {
    const list = settings[field] ?? [];
    for (let i = 1; i <= layout.counts[field]; i++) {
      const name = `${PREFIX[field]}${i}`;
      let val = list[i - 1] ?? '';
      if (isLlp) {
        if (field === 'reportPoints' && (hasLegacyLlpPoints || !val || LEGACY_LLP_REPORT_POINTS.has(val))) {
          val = getNoteTitle(name, 'llp');
        } else if (
          field === 'accountantReport' &&
          (hasLegacyLlpAccountant || !val || LEGACY_LLP_ACCOUNTANT_REPORT.has(val))
        ) {
          val = getNoteTitle(name, 'llp');
        } else if (field === 'auditorReport' && (hasLegacyLlpAuditor || !val || LEGACY_LLP_AUDITOR_REPORT.has(val))) {
          val = getNoteTitle(name, 'llp');
        }
      }
      values[name] = val;
    }
  }
  return values;
}

function toSettings(layout: ReportNotesLayout, raw: Values): ReportNotesSettings {
  const list = (field: ReportNotesListField): string[] =>
    Array.from({ length: layout.counts[field] }, (_, i) => String(raw[`${PREFIX[field]}${i + 1}`] ?? '').trim());

  return {
    reportPoints: list('reportPoints'),
    accountantReportType: Number(raw.accountantReportType ?? 0),
    accountantReport: list('accountantReport'),
    auditorReportType: Number(raw.auditorReportType ?? 0),
    auditorReport: list('auditorReport'),
    profitAndLossPolicies: list('profitAndLossPolicies'),
    balanceSheetPolicies: list('balanceSheetPolicies'),
  };
}

export interface ReportNotesForm {
  layout: ReportNotesLayout;
  /** The settings key (e.g. `'limited-uk-gaap'`) — used to look up catalogue placeholder text per field. */
  settingsKey: ReportNotesKey;
  values: Values;
  setValue: (name: string, value: string) => void;
  setRadioType: (name: 'accountantReportType' | 'auditorReportType', value: string) => void;
  markTouched: (name: string) => void;
  /** Whether this specific control's own pencil is open for editing. */
  isEditing: (name: string) => boolean;
  /** Toggles this specific control's pencil or opens the edit note popup. */
  onEdit: (name: string) => void;
  /** AP parity: opens the edit note popup with Title, Note Contents, Reset Default, and Capium Tags. */
  openNoteDialog: (name: string) => void;
  isDisabled: (name: string) => boolean;
  /** Angular `ng-invalid ng-touched`: an enabled, touched control that fails required / maxLength(255). */
  isInvalid: (name: string) => boolean;
  /** Records the last-focused text control, so `insertTag` knows where a clicked Capium tag should land. */
  setFocused: (name: string) => void;
  /** True while any control belonging to `tab` is being edited; gates the Capium Tags panel for that tab. */
  isEditingAny: (tab: string) => boolean;
  /**
   * ap5-a5: appends a Capium tag (see `core/models/capium-tags`) to the last-focused control of `tab`, falling back
   * to the tab's first text control when nothing in it has focus yet. Radio-only controls (the report-type groups)
   * are never a target. The result is capped at 255 characters, matching the fields' own `maxLength`.
   */
  insertTag: (tab: string, tag: string) => void;
}

function getSectionAndDefaultKey(name: string): { section: number; defaultNoteKey: string } {
  if (name.startsWith('reportPoint')) {
    return { section: 1, defaultNoteKey: name };
  }
  if (name.startsWith('accountantReport') || name.startsWith('auditorReport')) {
    return { section: 2, defaultNoteKey: name };
  }
  return { section: 3, defaultNoteKey: name };
}

/**
 * BUG-001: the note a field's pencil edits. The API names each layout's fields (`control`) - the accountant's report note of a
 * partnership is not the limited company's, an LLP's "Branches outside the UK" has a note of its own - so a field is matched
 * to its note by name and never guessed from its title or position, which saved one field's wording into a neighbour's note.
 */
function findNoteRow(
  name: string,
  noteTexts: FirmNoteTextRow[],
): { row: FirmNoteTextRow | undefined; section: number; noteKey: string } {
  const { section, defaultNoteKey } = getSectionAndDefaultKey(name);
  const row =
    noteTexts.find((r) => r.control?.toLowerCase() === name.toLowerCase()) ??
    noteTexts.find((r) => r.noteKey.toLowerCase() === name.toLowerCase());
  return { row, section: row?.section ?? section, noteKey: row?.noteKey ?? defaultNoteKey };
}

/** APT312: the client type (ClientTypes) of an entity's firm-wide notes, for the Capium Tags it offers; the limited-company keys are absent (full list). */
const NOTES_KEY_CLIENT_TYPE: Partial<Record<ReportNotesKey, number>> = { 'sole-trader': 1, partnership: 2, llp: 3 };

/**
 * Angular `ReportNotesSettingsBase`: firm-wide report headings and accounting policy titles for one business type.
 * Owner-requested addition beyond Angular parity (see PARITY_DECISIONS): every field has its own independent pencil,
 * read-only until clicked; clicking it opens the edit note popup with Title + Note Contents + Reset Default + Capium Tags.
 * The layout must be a module-level constant (the controls are derived from it once).
 */
export function useReportNotesSettings(settingsKey: ReportNotesKey, layout: ReportNotesLayout): ReportNotesForm {
  const [values, setValues] = useState<Values>(() => initialValues(layout));
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [focusedName, setFocusedName] = useState<string | null>(null);
  const [noteTexts, setNoteTexts] = useState<FirmNoteTextRow[]>([]);
  const tabOf = useRef(tabOfControls(layout)).current;
  const mounted = useRef(true);

  const apply = useCallback(
    (response: ReportNotesSettingsResponse) => {
      // form.patchValue: only the listed controls change (every control is listed).
      setValues((current) => ({ ...current, ...toValues(layout, response, settingsKey) }));
    },
    [layout, settingsKey],
  );

  useEffect(() => {
    mounted.current = true;
    firmSettingsService
      .getReportNotes(settingsKey)
      .then((response) => {
        if (!mounted.current) return;
        setEditing({});
        apply(response);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });

    firmSettingsService
      .getNoteTexts(settingsKey)
      .then((rows) => {
        if (!mounted.current) return;
        if (Array.isArray(rows)) {
          setNoteTexts(rows);
        }
      })
      .catch(() => undefined);

    return () => {
      mounted.current = false;
    };
  }, [settingsKey, apply]);

  /** Radio groups are enabled; note titles are edited via popup dialog. */
  const isDisabled = (name: string): boolean => {
    if (name === 'accountantReportType' || name === 'auditorReportType') return false;
    return true;
  };

  const isInvalid = (name: string): boolean => {
    if (isDisabled(name) || name.endsWith('ReportType')) return false;
    const value = values[name] ?? '';
    return value.trim().length === 0 || value.length > 255;
  };

  const saveFromDialog = (
    name: string,
    result: NoteEditorDialogResult,
    section: number,
    noteKey: string,
    defaultContent: string,
  ) => {
    // A blank or spaces-only title is never saved (the popup refuses it too; this is the guard behind it).
    if (!validateForm((result.title ?? '').trim().length > 0)) {
      setTouched((current) => new Set(current).add(name));
      return;
    }
    const nextValues = { ...values, [name]: result.title.trim() };
    setValues(nextValues);

    firmSettingsService
      .saveReportNotes(settingsKey, toSettings(layout, nextValues))
      .then((response) => {
        if (!mounted.current) return;
        apply(response);
        notifySuccess('Settings saved successfully');
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });

    const isReset = result.content === defaultContent;
    const saveContent = isReset
      ? firmSettingsService.resetNoteText(settingsKey, section, noteKey)
      : firmSettingsService.saveNoteText(settingsKey, section, noteKey, result.content);

    saveContent
      .then((updatedRows) => {
        if (!mounted.current) return;
        if (updatedRows && Array.isArray(updatedRows)) {
          setNoteTexts(updatedRows);
        }
      })
      .catch((error) => {
        if (!mounted.current) return;
        if (!isReset) {
          notifyError(error);
          return;
        }
        firmSettingsService
          .saveNoteText(settingsKey, section, noteKey, result.content)
          .then((updatedRows) => {
            if (mounted.current && updatedRows && Array.isArray(updatedRows)) {
              setNoteTexts(updatedRows);
            }
          })
          .catch((saveError) => {
            if (mounted.current) notifyError(saveError);
          });
      });
  };

  const setRadioType = (name: 'accountantReportType' | 'auditorReportType', value: string) => {
    const nextValues = { ...values, [name]: value };
    setValues(nextValues);
    firmSettingsService
      .saveReportNotes(settingsKey, toSettings(layout, nextValues))
      .then((response) => {
        if (!mounted.current) return;
        apply(response);
        notifySuccess('Settings saved successfully');
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  };

  const openNoteDialog = (name: string) => {
    const { row, section, noteKey } = findNoteRow(name, noteTexts);
    const noteDef = getNoteDefault(name, settingsKey);
    // BUG-001: the default Title and Note Contents are those of THIS layout (the API's, falling back to the catalogue's own).
    const defaultTitle = row?.defaultTitle || noteDef.title || getNoteTitle(name, settingsKey) || values[name] || name;
    const currentTitle = values[name] || defaultTitle;
    const currentContent = row?.content ?? noteDef.content ?? '';
    const defaultContent = row?.catalogueContent ?? noteDef.content ?? '';

    // APT312: the firm's default wording for an entity type offers that type's tags (the limited-company keys keep the full list).
    const tagGroups = capiumTagGroupsFor(NOTES_KEY_CLIENT_TYPE[settingsKey]);

    const data: NoteEditorDialogData = {
      heading: currentTitle,
      title: currentTitle,
      content: currentContent,
      defaultTitle,
      defaultContent,
      tagGroups,
      maxTitleLength: 80,
    };

    openDialog<NoteEditorDialogData, NoteEditorDialogResult>(PrinciplesActivitesDialog, {
      disableClose: true,
      data,
    })
      .afterClosed()
      .then((result) => {
        if (!result) return;
        saveFromDialog(name, result, section, noteKey, defaultContent);
      });
  };

  return {
    layout,
    settingsKey,
    values,
    setValue: (name, value) => setValues((current) => ({ ...current, [name]: value })),
    setRadioType,
    markTouched: (name) => setTouched((current) => (current.has(name) ? current : new Set(current).add(name))),
    isEditing: (name) => !!editing[name],
    onEdit: (name) => {
      openNoteDialog(name);
    },
    openNoteDialog,
    isDisabled,
    isInvalid: (name) => touched.has(name) && isInvalid(name),
    setFocused: (name) => setFocusedName(name),
    isEditingAny: (tab) => Object.entries(editing).some(([name, isOpen]) => isOpen && tabOf[name] === tab),
    insertTag: (tab, tag) => {
      const targets = textControlNames(layout, tab);
      if (targets.length === 0) return;
      const target = focusedName && targets.includes(focusedName) ? focusedName : targets[0]!;
      setValues((current) => ({
        ...current,
        [target]: appendCapiumTag(current[target] ?? '', tag).slice(0, 255),
      }));
    },
  };
}

// ---- shared markup ----

const noSubmit = (event: FormEvent) => event.preventDefault();

/** `<form [formGroup]="form">`: submitting never navigates. */
export function NotesForm({ children }: { children: ReactNode }) {
  return <form onSubmit={noSubmit}>{children}</form>;
}

/** The edit pencil: clicking opens the edit note popup dialog with Title + Note Contents + Reset Default + Capium Tags. */
function Pencil({ notes, name }: { notes: ReportNotesForm; name: string }) {
  return (
    <Button title="Edit" onClick={() => notes.openNoteDialog(name)}>
      <Icon name="edit" outlined />
    </Button>
  );
}

interface NotesInputProps {
  notes: ReportNotesForm;
  name: string;
  /** Width class (`w-434` report points, `w-364` everything else). */
  width: 'w-434' | 'w-364';
}

/**
 * One `form-field` row: a required, maxlength-255 input named like the Angular control, plus its own pencil. An
 * indented (sub-item) row is 14px in and 14px narrower, so its pencil stays in line with the rows around it.
 */
export function NotesInput({ notes, name, width }: NotesInputProps) {
  const indented = notes.layout.indented?.includes(name) ?? false;
  const placeholder = getNoteTitle(name, notes.settingsKey);
  return (
    <div className={`form-field d-flex align-items-center gap-15${indented ? ' notes-sub-item' : ''}`}>
      <InputField
        fieldClassName={`${indented && width === 'w-364' ? 'w-350' : width} gray-backclr`}
        data-control={name}
        value={notes.values[name] ?? ''}
        placeholder={placeholder || undefined}
        onChange={(event) => notes.setValue(name, event.target.value)}
        onFocus={() => notes.setFocused(name)}
        onBlur={() => notes.markTouched(name)}
        disabled={notes.isDisabled(name)}
        invalid={notes.isInvalid(name)}
        required
        maxLength={255}
      />
      <Pencil notes={notes} name={name} />
    </div>
  );
}

/** Inputs `<prefix>1..count`; every row renders its own independent pencil. */
export function NotesInputs({
  notes,
  prefix,
  from = 1,
  to,
  width,
}: {
  notes: ReportNotesForm;
  prefix: string;
  from?: number;
  to: number;
  width: 'w-434' | 'w-364';
}) {
  const rows: ReactNode[] = [];
  for (let i = from; i <= to; i++) {
    rows.push(<NotesInput key={i} notes={notes} name={`${prefix}${i}`} width={width} />);
  }
  return <>{rows}</>;
}

const ACCOUNTANT_REPORT_TYPES: { label: string; value: string }[] = [
  { label: 'Other/None', value: '0' },
  { label: 'ACCA', value: '1' },
  { label: 'ICAEW', value: '2' },
  { label: 'ICAS', value: '3' },
  { label: 'CIMA', value: '4' },
  { label: 'AAT', value: '5' },
  { label: 'AIA', value: '6' },
  { label: 'IFA', value: '7' },
];

const AUDITOR_REPORT_TYPES: { label: string; value: string }[] = [
  { label: 'Other/None', value: '0' },
  { label: 'ACCA', value: '1' },
  { label: 'ICAEW', value: '2' },
  { label: 'ICAS', value: '3' },
  { label: 'AAT', value: '5' },
  { label: 'AIA', value: '6' },
  { label: 'IFA', value: '7' },
];

/** `mat-radio-group formControlName="accountantReportType | auditorReportType"` (string values "0".."7"). */
function ReportTypeRadios({
  notes,
  name,
}: {
  notes: ReportNotesForm;
  name: 'accountantReportType' | 'auditorReportType';
}) {
  const options = name === 'auditorReportType' ? AUDITOR_REPORT_TYPES : ACCOUNTANT_REPORT_TYPES;
  return (
    <RadioGroup
      name={name}
      value={notes.values[name]}
      onChange={(value) => notes.setRadioType(name, String(value))}
      disabled={notes.isDisabled(name)}
    >
      <div className="d-flex align-center gap-10">
        {options.map(({ label, value }) => (
          <Radio key={label} value={value} hostClassName="example-radio-button">
            {label}
          </Radio>
        ))}
      </div>
    </RadioGroup>
  );
}

/** A `.llp-box` column with a heading (`P&L Accounting Policies`, `Accountant’s Report`, ...). */
function NotesBox({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <div className="llp-box">
      <div className="form-field pt-1 pb-5 hr-bottom">
        <p>{heading}</p>
      </div>
      {children}
    </div>
  );
}

/** Director's / Member's report points tab (limited companies and LLP). */
export function ReportPointsTab({
  notes,
  heading,
  bodyClassName,
  separatorAfter,
}: {
  notes: ReportNotesForm;
  heading: string;
  bodyClassName: string;
  /** Limited companies draw a rule after this row (the source has it after report point 6). */
  separatorAfter?: number;
}) {
  const count = notes.layout.counts.reportPoints;
  return (
    <NotesForm>
      <div className="d-flex gap-15">
        <div className={bodyClassName}>
          <div className="sidenav-body-content p-0">
            <div className="form-field-title pb-10">
              <p>{heading}</p>
            </div>
            <div>
              {separatorAfter === undefined ? (
                <NotesInputs notes={notes} prefix="reportPoint" to={count} width="w-434" />
              ) : (
                <>
                  <NotesInputs notes={notes} prefix="reportPoint" to={separatorAfter} width="w-434" />
                  <div className="hr-top mt-2 pb-2"></div>
                  <NotesInputs notes={notes} prefix="reportPoint" from={separatorAfter + 1} to={count} width="w-434" />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </NotesForm>
  );
}

/** `Accountant / Auditor’s Report` tab: two boxes with the institute radios and the report lines. */
export function AccountantAuditorTab({ notes, rowClassName }: { notes: ReportNotesForm; rowClassName: string }) {
  const { counts } = notes.layout;
  return (
    <NotesForm>
      <div className="d-flex gap-15">
        <div className={rowClassName}>
          <NotesBox heading="Accountant’s Report">
            <ReportTypeRadios notes={notes} name="accountantReportType" />
            <NotesInputs notes={notes} prefix="accountantReport" to={counts.accountantReport} width="w-364" />
          </NotesBox>
          <NotesBox heading="Auditor’s Report">
            <ReportTypeRadios notes={notes} name="auditorReportType" />
            <NotesInputs notes={notes} prefix="auditorReport" to={counts.auditorReport} width="w-364" />
          </NotesBox>
        </div>
      </div>
    </NotesForm>
  );
}

/** The partnership / sole trader accountant's report tab: two plain lines, no heading and no radios. */
export function AccountantLinesTab({ notes }: { notes: ReportNotesForm }) {
  return (
    <NotesForm>
      <div className="d-flex gap-15">
        <div className="sidenav-body pt-10">
          <div className="sidenav-body-content p-0">
            <NotesInputs
              notes={notes}
              prefix="accountantReport"
              to={notes.layout.counts.accountantReport}
              width="w-364"
            />
          </div>
        </div>
      </div>
    </NotesForm>
  );
}

/** `Accounting Policy` tab: P&L policies and balance sheet policies side by side. */
export function PolicyTab({ notes, rowClassName }: { notes: ReportNotesForm; rowClassName: string }) {
  const { counts } = notes.layout;
  return (
    <NotesForm>
      <div className="d-flex gap-15">
        <div className={rowClassName}>
          <NotesBox heading="P&L Accounting Policies">
            <NotesInputs notes={notes} prefix="plPolicy" to={counts.profitAndLossPolicies} width="w-364" />
          </NotesBox>
          <NotesBox heading="Balance Sheet Accounting Policies">
            <NotesInputs notes={notes} prefix="bsPolicy" to={counts.balanceSheetPolicies} width="w-364" />
          </NotesBox>
        </div>
      </div>
    </NotesForm>
  );
}
